// ─── Chapter Change Classifier ────────────────────────────────────────────────
// Distinguishes MINOR edits (typos, spacing, punctuation) from MAJOR edits
// (paragraph rewrites, figure changes, citation additions, heading changes).
//
// Used in save() to decide whether to invalidate chapter approval.
// MINOR edits preserve APPROVED status. MAJOR edits revert to DRAFT.

// ── Text extraction ───────────────────────────────────────────────────────────

function stripToText(html) {
  if (!html) return ''
  return html
    .replace(/<div[^>]*data-thesis-figure[^>]*>[\s\S]*?<\/div>/gi, '[[FIGURE]]')
    .replace(/<table[\s\S]*?<\/table>/gi, '[[TABLE]]')
    .replace(/<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/gi, '\n[[HEADING:$1]]\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function tokenize(text) {
  return text.split(/\s+/).filter(Boolean)
}

// ── Structural fingerprints ───────────────────────────────────────────────────

function extractHeadings(html) {
  const matches = [...(html || '').matchAll(/<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/gi)]
  return matches.map(m => m[1].replace(/<[^>]+>/g, '').trim().toLowerCase())
}

function countFigures(html) {
  return ((html || '').match(/data-thesis-figure="true"/g) || []).length
}

function countTables(html) {
  return ((html || '').match(/<table/gi) || []).length
}

function extractCitations(text) {
  return (text.match(/\([A-Za-z][^)]*,\s*\d{4}\)/g) || [])
}

function extractStatNumbers(text) {
  // β, p-values, percentages, regression coefficients
  return (text.match(/β\s*=\s*[-\d.]+|p\s*[<>=]\s*[\d.]+|\d+\.\d{2,}%|R²\s*=\s*[\d.]+/g) || [])
}

// ── Levenshtein ratio ─────────────────────────────────────────────────────────

function changeRatio(wordsA, wordsB) {
  if (!wordsA.length && !wordsB.length) return 0
  const total = Math.max(wordsA.length, wordsB.length)
  const setA  = new Set(wordsA)
  const setB  = new Set(wordsB)
  const common = [...setA].filter(w => setB.has(w)).length
  return 1 - common / Math.max(setA.size, setB.size)
}

function onlyPunctuationOrSpaceChanged(textA, textB) {
  const normalize = t => t.replace(/[.,;:!?'"()\-–—…\s]+/g, ' ').trim()
  return normalize(textA) === normalize(textB)
}

// ── Main classifier ───────────────────────────────────────────────────────────

/**
 * Classify the semantic impact of a content change.
 *
 * @param {string} previousHtml — previous editor HTML
 * @param {string} nextHtml     — new editor HTML
 * @returns {{ type: 'MINOR' | 'MAJOR', reason: string, changeRatio: number }}
 */
export function classifyEdit(previousHtml, nextHtml) {
  if (!previousHtml && !nextHtml) return { type: 'MINOR', reason: 'no content', changeRatio: 0 }
  if (!previousHtml) return { type: 'MAJOR', reason: 'new content added', changeRatio: 1 }

  // ── Structural checks (always MAJOR) ────────────────────────────────────────

  const prevHeadings = extractHeadings(previousHtml)
  const nextHeadings = extractHeadings(nextHtml)
  if (JSON.stringify(prevHeadings) !== JSON.stringify(nextHeadings)) {
    return { type: 'MAJOR', reason: 'heading structure changed', changeRatio: 1 }
  }

  const figDiff = Math.abs(countFigures(nextHtml) - countFigures(previousHtml))
  if (figDiff > 0) {
    return { type: 'MAJOR', reason: `figure count changed (${figDiff > 0 ? '+' : ''}${figDiff})`, changeRatio: 1 }
  }

  const tblDiff = Math.abs(countTables(nextHtml) - countTables(previousHtml))
  if (tblDiff > 0) {
    return { type: 'MAJOR', reason: `table count changed`, changeRatio: 1 }
  }

  // ── Plain-text analysis ──────────────────────────────────────────────────────

  const prevText = stripToText(previousHtml)
  const nextText = stripToText(nextHtml)

  if (prevText === nextText) {
    return { type: 'MINOR', reason: 'identical content (formatting only)', changeRatio: 0 }
  }

  // Pure punctuation / spacing fix
  if (onlyPunctuationOrSpaceChanged(prevText, nextText)) {
    return { type: 'MINOR', reason: 'punctuation or spacing adjustment', changeRatio: 0 }
  }

  // Statistical numbers changed
  const prevStats = extractStatNumbers(prevText)
  const nextStats = extractStatNumbers(nextText)
  if (JSON.stringify(prevStats) !== JSON.stringify(nextStats)) {
    return { type: 'MAJOR', reason: 'statistical values changed', changeRatio: 1 }
  }

  // Citation pattern changed
  const prevCites = extractCitations(prevText)
  const nextCites = extractCitations(nextText)
  if (prevCites.length !== nextCites.length) {
    return { type: 'MAJOR', reason: 'citation count changed', changeRatio: 1 }
  }

  // Word-level change ratio
  const prevWords = tokenize(prevText)
  const nextWords = tokenize(nextText)

  // Large length change (>15% word count difference)
  const lengthRatio = Math.abs(prevWords.length - nextWords.length) / Math.max(prevWords.length, 1)
  if (lengthRatio > 0.15) {
    return { type: 'MAJOR', reason: `content length changed by ${Math.round(lengthRatio * 100)}%`, changeRatio: lengthRatio }
  }

  const ratio = changeRatio(prevWords, nextWords)

  // High lexical change = MAJOR
  if (ratio > 0.12) {
    return { type: 'MAJOR', reason: `significant content revision (~${Math.round(ratio * 100)}% of words changed)`, changeRatio: ratio }
  }

  // Small change = MINOR (likely typo fix, word swap, clarification)
  return { type: 'MINOR', reason: `minor revision (${Math.round(ratio * 100)}% of words)`, changeRatio: ratio }
}

/**
 * Quick check: is this edit significant enough to warrant showing a toast?
 * Use to suppress "reverted to draft" for trivial saves.
 */
export function isSignificantEdit(previousHtml, nextHtml) {
  const { type } = classifyEdit(previousHtml, nextHtml)
  return type === 'MAJOR'
}
